import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';

export type FAQItem = {
  question: string;
  answer: string;
  items?: string[];
};

export const FAQ_CONTENT: FAQItem[] = [
  {
    question: 'What is Shinkai?',
    answer:
      'Shinkai is a local-first AI protocol that combines execution, identity, and payments into one interoperable network.',
    items: [
      'Local-first execution with privacy by design.',
      'Multi-network AI connectivity through a single interface.',
      'Built-in payments, incentives, and agent marketplaces.',
      'Portable agents you can move across apps and devices.',
    ],
  },
  {
    question: 'What are Shinkai identities?',
    answer:
      'Shinkai identities are cryptographic identifiers that let you prove ownership, sign requests, and build reputation across the network.',
    items: [
      'Tied to your keys and wallet for verifiable ownership.',
      'Portable across Shinkai Web, Desktop, and protocol apps.',
      'Used to stake, earn rewards, and build agent reputation.',
      'Designed for on-chain and off-chain interoperability.',
    ],
  },
  {
    question: 'How do I earn rewards?',
    answer:
      'Link your Shinkai identity and opt-in to the rewards program to start earning points. Complete quests by using Shinkai Web, creating agents, and exploring the ecosystem. Points are tracked on the leaderboard and may unlock future benefits.',
  },
  {
    question: 'Is there a cost to mint?',
    answer:
      'Minting on Base Sepolia (Testnet) is free, but requires a small amount of testnet ETH for gas fees. Mainnet launch details will be announced soon.',
  },
];

function FAQItemComponent({ item }: { item: FAQItem }) {
  return (
    <Collapsible className="group rounded-xl border border-divider bg-bg-secondary overflow-hidden">
      <CollapsibleTrigger className="flex w-full items-center justify-between p-5 text-left text-sm font-medium text-text-default hover:bg-white/5 transition-colors">
        {item.question}
        <ChevronDown className="h-4 w-4 shrink-0 text-text-tertiary transition-transform duration-200 group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
        <div className="px-5 pb-5 text-sm text-text-secondary leading-relaxed space-y-3">
          <p>{item.answer}</p>
          {item.items && item.items.length > 0 && (
            <ul className="list-disc pl-5 space-y-1 text-text-tertiary">
              {item.items.map((listItem) => (
                <li key={listItem}>{listItem}</li>
              ))}
            </ul>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default function FAQ() {
  return (
    <section
      id="faq"
      className="max-w-3xl mx-auto w-full space-y-8 py-12 pb-20 scroll-mt-24"
    >
      <div className="text-center space-y-1">
        <h2 className="font-clash text-3xl font-medium leading-[1.1] tracking-tight text-white">
          Frequently Asked Questions
        </h2>
        <p className="text-base text-text-secondary">
          Everything you need to know about Shinkai ID.
        </p>
      </div>

      <div className="space-y-3">
        {FAQ_CONTENT.map((item) => (
          <FAQItemComponent key={item.question} item={item} />
        ))}
      </div>
    </section>
  );
}
